import React from "react";
import { Clock, AlertTriangle, TrendingDown, Users } from "lucide-react";
import { Card } from "./ui/card";

const ProblemSection = () => {
    const problems = [
        {
            icon: Clock,
            iconColor: "text-red-600",
            bgColor: "bg-red-50",
            title: "Manual Reporting Overhead",
            stat: "6+ hours/week",
            description:
                "Engineering leaders spend valuable time manually compiling status updates, release notes, and sprint reports instead of focusing on strategy.",
            impact: "Lost productivity",
        },
        {
            icon: AlertTriangle,
            iconColor: "text-orange-600",
            bgColor: "bg-orange-50",
            title: "Lack of Real-Time Visibility",
            stat: "2-3 day lag",
            description:
                "By the time you understand what's blocking your team or why a sprint is behind, it's too late to course-correct effectively.",
            impact: "Missed deadlines",
        },
        {
            icon: TrendingDown,
            iconColor: "text-amber-600",
            bgColor: "bg-amber-50",
            title: "Inefficient Resource Allocation",
            stat: "30% mismatch",
            description:
                "Without deep context on past work and expertise, assigning tasks to the right engineers becomes guesswork, leading to longer delivery times.",
            impact: "Reduced velocity",
        },
        {
            icon: Users,
            iconColor: "text-yellow-600",
            bgColor: "bg-yellow-50",
            title: "Scattered Information",
            stat: "6+ tools",
            description:
                "Critical information lives across Jira, GitHub, Slack, Gmail, and more. No single source of truth means decisions are made on incomplete data.",
            impact: "Poor decisions",
        },
    ];

    return (
        <section className="py-24 bg-slate-900 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl"></div>
                <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500 rounded-full filter blur-3xl"></div>
            </div>

            <div className="relative max-w-7xl mx-auto px-6">
                {/* Section header */}
                <div className="text-center mb-16 max-w-3xl mx-auto">
                    <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                        Engineering Leadership Shouldn't Feel Like{" "}
                        <span className="bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">
                            Detective Work
                        </span>
                    </h2>
                    <p className="text-xl text-slate-300 leading-relaxed">
                        You're managing complex engineering operations, but your
                        tools don't talk to each other. Every question requires
                        manual data gathering from multiple sources.
                    </p>
                </div>

                {/* Problems grid */}
                <div className="grid md:grid-cols-2 gap-6">
                    {problems.map((problem, idx) => {
                        const Icon = problem.icon;
                        return (
                            <Card
                                key={idx}
                                className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 hover:border-slate-600 p-8 transition-all duration-300 hover:shadow-xl hover:shadow-blue-900/20 group"
                            >
                                <div className="flex items-start gap-4">
                                    <div
                                        className={`flex-shrink-0 w-14 h-14 ${problem.bgColor} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}
                                    >
                                        <Icon
                                            className={`w-7 h-7 ${problem.iconColor}`}
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-start justify-between mb-3">
                                            <h3 className="text-xl font-bold text-white">
                                                {problem.title}
                                            </h3>
                                            <span className="text-sm font-semibold text-red-400 bg-red-900/30 px-3 py-1 rounded-full">
                                                {problem.stat}
                                            </span>
                                        </div>
                                        <p className="text-slate-300 mb-3 leading-relaxed">
                                            {problem.description}
                                        </p>
                                        <div className="flex items-center gap-2 text-sm">
                                            <span className="text-slate-500">
                                                Impact:
                                            </span>
                                            <span className="text-orange-400 font-semibold">
                                                {problem.impact}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                </div>

                {/* Bottom CTA hint */}
                <div className="mt-16 text-center">
                    <p className="text-slate-400 text-lg">
                        Sound familiar? There's a better way. ↓
                    </p>
                </div>
            </div>
        </section>
    );
};

export default ProblemSection;
